import { useState, useEffect } from "react";
import { getAuditLog } from "../../api/admin";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { formatDistanceToNow, format } from "date-fns";

const ACTION_TYPES = [
  { value: "", label: "All Actions" },
  { value: "admin.create_user", label: "User Created" },
  { value: "admin.approve", label: "User Approved" },
  { value: "admin.revoke", label: "User Revoked" },
  { value: "admin.delete_user", label: "User Deleted" },
  { value: "admin.edit_user", label: "User Edited" },
  { value: "admin.reset_password", label: "Password Reset" },
  { value: "admin.invite_user", label: "User Invited" },
  { value: "admin.resend_invitation", label: "Invitation Resent" },
  { value: "admin.revoke_invitation", label: "Invitation Revoked" },
  { value: "admin.approve_join_request", label: "Join Request Approved" },
  { value: "admin.reject_join_request", label: "Join Request Rejected" },
  { value: "login", label: "Login" },
  { value: "register", label: "Registration" },
];

const PAGE_SIZE = 50;

/**
 * AdminAuditPage — org-wide audit log with filtering and pagination.
 */
export default function AdminAuditPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [offset, setOffset] = useState(0);
  const [actionFilter, setActionFilter] = useState("");

  useEffect(() => {
    loadAuditLog();
  }, [offset, actionFilter]);

  const loadAuditLog = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: PAGE_SIZE, offset };
      if (actionFilter) params.action = actionFilter;
      const data = await getAuditLog(params);
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      console.error("Failed to load audit log:", err);
      setError(err?.response?.data?.detail || "Failed to load audit log");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (value) => {
    setActionFilter(value);
    setOffset(0);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  if (error) {
    return (
      <div>
        <h2 className="font-cabinet text-2xl font-black tracking-tight text-slate-900 mb-6">
          Audit Log
        </h2>
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-sm">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-w-0">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="font-cabinet text-2xl font-black tracking-tight text-slate-900">
          Audit Log
        </h2>
        <div className="flex w-full items-center gap-3 sm:w-auto">
          <Select value={actionFilter} onValueChange={handleFilterChange}>
            <SelectTrigger className="w-full rounded-sm sm:w-52" data-testid="audit-action-filter">
              <SelectValue placeholder="Filter by action" />
            </SelectTrigger>
            <SelectContent>
              {ACTION_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card className="min-w-0 overflow-hidden border-slate-200 rounded-sm">
        <CardContent className="min-w-0 p-0">
          {loading ? (
            <div className="p-6 space-y-3">
              {[...Array(8)].map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-slate-500 italic">
              No audit entries found
            </div>
          ) : (
            <Table
              className="min-w-[720px]"
              containerTestId="audit-log-table-scroll"
              data-testid="audit-log-table"
            >
              <TableHeader>
                <TableRow>
                  <TableHead className="font-semibold">Timestamp</TableHead>
                  <TableHead className="font-semibold">Actor</TableHead>
                  <TableHead className="font-semibold">Action</TableHead>
                  <TableHead className="font-semibold">Target / Detail</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="text-xs text-slate-600 whitespace-nowrap">
                      {entry.timestamp ? (
                        <span title={format(new Date(entry.timestamp), "PPpp")}>
                          {formatDistanceToNow(new Date(entry.timestamp), { addSuffix: true })}
                        </span>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-slate-700">
                      {entry.user_email}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="rounded-sm text-xs">
                        {entry.action}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600 max-w-xs truncate">
                      {entry.detail || "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-slate-500">
            Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total} entries
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              disabled={offset === 0}
              className="rounded-sm"
            >
              Previous
            </Button>
            <span className="text-sm text-slate-600">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOffset(offset + PAGE_SIZE)}
              disabled={offset + PAGE_SIZE >= total}
              className="rounded-sm"
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
